/**
 * Version Check Hook
 * Checks for app updates by comparing version.json timestamps
 * Forces reload when a new version is detected on the server
 */

import { useEffect, useState } from 'react';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_CHECK_URL = '/version.json';

export const useVersionCheck = () => {
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);

  useEffect(() => {
    let intervalId;

    const checkVersion = async () => {
      try {
        // Fetch version.json with cache-busting query parameter
        const response = await fetch(`${VERSION_CHECK_URL}?t=${Date.now()}`);
        const data = await response.json();
        
        const serverVersion = data.version;
        const storedVersion = localStorage.getItem('appVersion');

        if (process.env.NODE_ENV !== 'production') {
          console.log('Version check:', {
            server: serverVersion,
            stored: storedVersion
          });
        }

        // First time - store the version
        if (!storedVersion) {
          localStorage.setItem('appVersion', serverVersion);
          setCurrentVersion(serverVersion);
          return;
        }

        // Version changed - need to update
        if (serverVersion !== storedVersion) {
          console.log('New version detected:', serverVersion);
          setNeedsUpdate(true);
          
          // Auto-reload after 3 seconds
          setTimeout(() => {
            console.log('Reloading to apply update...');
            localStorage.setItem('appVersion', serverVersion);
            window.location.reload(true);
          }, 3000);
        }
      } catch (error) {
        // Version check is best-effort and explicitly non-blocking — a transient
        // network failure (offline, version.json briefly unreachable) is expected
        // and not actionable, so don't pollute the console with a red error.
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Version check failed (non-blocking):', error?.message || error);
        }
      }
    };

    // Check on mount
    checkVersion();

    // Check periodically
    intervalId = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  return { needsUpdate, currentVersion };
};
